import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import { useSelector } from "react-redux";

const MetaDataCard = ({ title, value }) => {
  if (!value || value === "N/A") return null;

  return (
    <div className="bg-neutral-800/40 border border-neutral-800 rounded-lg p-4">
      <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">
        {title}
      </p>
      <p className="text-sm md:text-base text-neutral-200 font-medium">
        {value}
      </p>
    </div>
  );
};

const MovieDetailsPage = () => {
  const { id } = useParams();
  const [movieInfo, setMovieInfo] = useState(null);
  const [trailerUrl, setTrailerUrl] = useState(null);
  const [trailerFetched, setTrailerFetched] = useState(false);
  const [infoFetched, setInfoFetched] = useState(false);
  const { data: user, isAuthChecked } = useSelector((store) => store.user);
  const navigate = useNavigate();
  const traktApiKey = process.env.REACT_APP_TRAKT_API_KEY;
  const omdbApiKey = process.env.REACT_APP_OMDB_API_KEY;

  useEffect(() => {
    if (isAuthChecked && !user) navigate("/");
  }, [isAuthChecked, user, navigate]);

  const movieTrailer = () => {
    fetch("https://api.trakt.tv/movies/" + id + "/videos", {
      headers: {
        "Content-Type": "application/json",
        "trakt-api-version": "2",
        "trakt-api-key": traktApiKey,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        const youtubeTrailer = data.find(
          (video) =>
            video.type.toLowerCase() === "trailer" &&
            video.site.toLowerCase() === "youtube",
        );

        if (youtubeTrailer) {
          const url = new URL(youtubeTrailer.url);
          const videoId = url.searchParams.get("v");
          if (videoId) {
            setTrailerUrl(`https://www.youtube.com/embed/${videoId}`);
          }
        }
      })
      .catch((error) => console.error("Error:", error))
      .finally(() => setTrailerFetched(true));
  };

  const movieMetaData = () => {
    fetch("https://www.omdbapi.com/?i=" + id + "&apikey=" + omdbApiKey)
      .then((res) => res.json())
      .then((res) => setMovieInfo(res))
      .finally(() => setInfoFetched(true));
  };

  useEffect(() => {
    movieTrailer();
    movieMetaData();
  }, [id]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!isAuthChecked) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-black text-white">
        <div className="h-14 w-14 border-4 border-neutral-700 border-t-red-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <>
      <Header />

      {!infoFetched || !trailerFetched ? (
        <div className="min-h-screen flex flex-col items-center justify-center bg-black text-white">
          <div className="flex flex-col items-center space-y-6">
            <div className="h-14 w-14 border-4 border-neutral-700 border-t-red-600 rounded-full animate-spin"></div>
            <p className="text-neutral-400 tracking-wide">
              Fetching movie details...
            </p>
          </div>
        </div>
      ) : (
        <div className="px-4 md:px-10 lg:px-32 bg-gradient-to-b from-black via-neutral-900 to-black text-white min-h-screen">
          <div className="max-w-6xl mx-auto">
            {/* Trailer Section */}
            {trailerUrl ? (
              <div className="pt-20 lg:pt-28">
                <div className="rounded-2xl overflow-hidden shadow-2xl border border-neutral-800">
                  <iframe
                    className="w-full h-64 md:h-[500px] lg:h-[650px]"
                    src={trailerUrl}
                    title="YouTube video player"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            ) : (
              trailerFetched &&
              !trailerUrl && (
                <div className="pt-24 lg:pt-28">
                  <div className=" mx-auto bg-neutral-900/60 border border-neutral-800 rounded-xl px-6 py-5 flex items-center justify-between">
                    <div>
                      <h2 className="text-base font-medium text-neutral-200">
                        Trailer unavailable
                      </h2>
                      <p className="text-sm text-neutral-500 mt-1">
                        No official trailer is available for this title.
                      </p>
                    </div>
                  </div>
                </div>
              )
            )}

            {/* Movie Content */}
            {movieInfo && (
              <div className="mt-16 bg-neutral-900/60 backdrop-blur-md border border-neutral-800 rounded-2xl shadow-xl p-6 md:p-10">
                <div className="flex flex-col md:flex-row gap-10">
                  {/* Poster */}
                  {movieInfo.Poster && movieInfo.Poster !== "N/A" && (
                    <div className="flex-shrink-0">
                      <img
                        src={movieInfo.Poster}
                        alt={movieInfo.Title}
                        className="w-64 rounded-xl shadow-lg object-cover"
                      />
                    </div>
                  )}

                  {/* Content */}
                  <div className="flex-1">
                    {/* Title + Quick Info */}
                    <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
                      {movieInfo.Title}
                    </h1>

                    <div className="flex flex-wrap items-center gap-3 text-sm text-neutral-400 mb-6">
                      {movieInfo.Year && <span>{movieInfo.Year}</span>}
                      {movieInfo.Runtime && <span>• {movieInfo.Runtime}</span>}
                      {movieInfo.Genre && <span>• {movieInfo.Genre}</span>}
                    </div>

                    {/* Overview */}
                    {movieInfo.Plot && (
                      <div className="mb-10">
                        <h2 className="text-lg font-semibold text-neutral-400 mb-3 uppercase tracking-wide">
                          Overview
                        </h2>
                        <p className="text-neutral-300 leading-relaxed border-l-2 border-neutral-700 pl-4">
                          {movieInfo.Plot}
                        </p>
                      </div>
                    )}

                    {/* Details Section */}
                    <div className="space-y-10">
                      <div>
                        <h2 className="text-lg font-semibold text-neutral-400 mb-4 uppercase tracking-wide">
                          Details
                        </h2>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <MetaDataCard
                            title="Language"
                            value={movieInfo.Language}
                          />
                          <MetaDataCard
                            title="Country"
                            value={movieInfo.Country}
                          />
                          <MetaDataCard
                            title="Runtime"
                            value={movieInfo.Runtime}
                          />
                          <MetaDataCard
                            title="Awards"
                            value={movieInfo.Awards}
                          />
                        </div>
                      </div>

                      <div>
                        <h2 className="text-lg font-semibold text-neutral-400 mb-4 uppercase tracking-wide">
                          Production
                        </h2>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <MetaDataCard
                            title="Director"
                            value={movieInfo.Director}
                          />
                          <MetaDataCard
                            title="Writer"
                            value={movieInfo.Writer}
                          />
                          <MetaDataCard
                            title="Box Office"
                            value={movieInfo.BoxOffice}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Cast */}
                    {movieInfo.Actors && (
                      <div className="mt-10">
                        <h2 className="text-lg font-semibold text-neutral-400 mb-4 uppercase tracking-wide">
                          Cast
                        </h2>

                        <div className="flex flex-wrap gap-3">
                          {movieInfo.Actors.split(",").map((actor, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 text-sm bg-neutral-800 border border-neutral-700 rounded-full text-neutral-300"
                            >
                              {actor.trim()}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Ratings */}
                    {movieInfo.Ratings?.length > 0 && (
                      <div className="mt-12">
                        <h2 className="text-lg font-semibold mb-6 tracking-wide text-neutral-300">
                          Ratings
                        </h2>

                        <div className="space-y-6">
                          {movieInfo.imdbRating && (
                            <div className="bg-neutral-800/60 border border-neutral-700 rounded-xl p-6 flex items-center justify-between">
                              <div>
                                <p className="text-sm text-neutral-400 uppercase tracking-wide">
                                  IMDb Rating
                                </p>
                                <p className="text-3xl font-bold mt-1">
                                  {movieInfo.imdbRating}
                                  <span className="text-neutral-400 text-lg">
                                    {" "}
                                    / 10
                                  </span>
                                </p>
                              </div>
                            </div>
                          )}

                          <div className="space-y-4">
                            {movieInfo.Ratings.map((rating, index) => {
                              let percentage = 0;

                              if (rating.Value.includes("%")) {
                                percentage = parseInt(
                                  rating.Value.replace("%", ""),
                                );
                              } else if (rating.Value.includes("/")) {
                                const [score, total] =
                                  rating.Value.split("/").map(Number);
                                percentage = (score / total) * 100;
                              }

                              return (
                                <div key={index} className="space-y-2">
                                  <div className="flex justify-between text-sm text-neutral-300">
                                    <span>{rating.Source}</span>
                                    <span>{rating.Value}</span>
                                  </div>

                                  <div className="w-full bg-neutral-800 rounded-full h-2 overflow-hidden">
                                    <div
                                      className="bg-neutral-400 h-full rounded-full"
                                      style={{ width: `${percentage}%` }}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default MovieDetailsPage;
